import React, { useEffect, useState } from 'react';
import Modal from '@/components/Modal';
import Button from '@/components/Button';
import TextArea from '@/components/TextArea';
import { rejectPaymentModal } from '@/utils/translations';

// Matches RqRejectMultiSignAction's @Size(max = 200) constraint on `notes`.
const MAX_NOTES_LENGTH = 200;

interface RejectPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  isSubmitting?: boolean;
  error?: string | null;
  // Multi-payee batch groups get "Reject batch" copy instead of the default "Reject payment" -
  // BatchOverviewModal passes true, ScheduledPaymentModal (individual payments only) doesn't.
  isBatch?: boolean;
}

const RejectPaymentModal: React.FC<RejectPaymentModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isSubmitting = false,
  error,
  isBatch = false
}) => {
  const [reason, setReason] = useState('');

  // Reset the field each time the dialog is (re)opened rather than leaving a stale reason
  // behind from a previous rejection attempt.
  useEffect(() => {
    if (isOpen) setReason('');
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      headerText={isBatch ? rejectPaymentModal.batchTitle : rejectPaymentModal.title}
      headerVariant="lg">
      <div className="flex flex-col gap-y-6 py-4">
        <p className="text-sm font-medium text-dark-navy-30">
          {isBatch ? rejectPaymentModal.batchDescription : rejectPaymentModal.description}
        </p>

        <div className="flex flex-col gap-y-2">
          <span className="text-xs font-medium text-emerge-navy">
            {rejectPaymentModal.reasonLabel}
          </span>
          <TextArea
            rows={3}
            value={reason}
            onTextChange={(value) => setReason(value.slice(0, MAX_NOTES_LENGTH))}
            maxAllowedCharacters={MAX_NOTES_LENGTH}
            className="border-emerge-teal bg-emerge-base-3"
          />
        </div>

        {error && (
          <p role="alert" className="text-xs font-medium text-error-darkred">
            {error}
          </p>
        )}

        <div className="flex items-center justify-center gap-x-5">
          <Button
            size="pill"
            className="w-[140px]"
            disabled={!reason.trim() || isSubmitting}
            onClick={() => onConfirm(reason.trim())}>
            {isSubmitting ? rejectPaymentModal.confirmingButton : rejectPaymentModal.confirmButton}
          </Button>
          <Button
            variant="primary_inverted"
            size="pill"
            className="w-[140px] border-aux-gray"
            disabled={isSubmitting}
            onClick={onClose}>
            {rejectPaymentModal.cancelButton}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default RejectPaymentModal;
